// ListCustomers.tsx
import React from 'react';
import styles from './ListCustomers.module.scss';
import useExternalApiService from 'helpers/ExternalApiService';
import { store } from 'stores/Store';
import { ReactComponent as UserIcon } from "assets/user2.svg";
import { ReactComponent as LeadIcon } from "assets/UserPlus.svg";
import { ReactComponent as MagnifyingGlassIcon } from 'assets/magnifyingGlass.svg';
import { ReactComponent as FunnelIcon } from 'assets/funnel.svg';
import { ReactComponent as BlueFunnelIcon } from 'assets/blueFunnel.svg';
import Funnel from './components/Funnel';
import Pipeline from './components/Pipeline';
import FilterCarousel from './components/FilterCarousel';
import FilterDropdown from './components/FilterDropdown';
import { ReactComponent as ExpandIcon } from 'assets/expand.svg';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, Checkbox } from '@mantine/core';

import { ReactComponent as CheckIcon } from "assets/verified.svg";
import { ReactComponent as FadersIcon } from "assets/faders.svg";
enum Tab {
  ALL = "all",
  RECENT = "recent",
  FUNNEL = "funnel",
  LEAD = "lead",
}

interface IListCustomersProps {
  onCustomerClick: (customer: any) => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  tab?: string
}

const ListCustomers = ({ onCustomerClick, tab, searchTerm, setSearchTerm }: IListCustomersProps) => {
  const [practitioners, setPractitioners] = React.useState<any[]>([]);
  const [leads, setLeads] = React.useState<any[]>([]);
  const [totalLeads, setTotalLeads] = React.useState(0);
  const [leadPage, setLeadPage] = React.useState(1);
  const [isLeadsLoading, setIsLeadsLoading] = React.useState(false);
  const [hasMoreLeads, setHasMoreLeads] = React.useState(true);

  const [selectedTab, setSelectedTab] = React.useState<Tab>(Tab.ALL);
  const [selectedOption, setSelectedOption] = React.useState('YTD');
  const [selectedOption2, setSelectedOption2] = React.useState('January');
  const [pipelineIsClicked, setPipelineIsClicked] = React.useState(false);
  const [funnelData, setFunnelData] = React.useState<any>(null);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(searchTerm);

  const [multiSelectOpen, setMultiSelectOpen] = React.useState(false);

  //this is the filter thats used in the filter dropdown
  const [multiSelectValue, setMultiSelectValue] = React.useState<string[]>(store.getPractitionersFilterMultiSelectValue());
 
  //this is the filter thats used in the filter carousel
  const [selectedFilter, setSelectedFilter] = React.useState(store.getPractitionersCarouselFilter());

  const LIMIT = 50;

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const navigate = useNavigate();

  // Track previous search term to avoid unnecessary cache clearing
  const prevSearchTermRef = React.useRef<string | null>(null);
  const prevSelectedFilterRef = React.useRef<string | null>(null);

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cache search term when it changes
  React.useEffect(() => {
    store.setCachedSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);
  
  React.useEffect(() => {
    store.setPractitionersFilterMultiSelectValue(multiSelectValue);
  }, [multiSelectValue]);

  // Hard-coded example data for Pipeline demo (you can remove or replace this)
  const dadosFunel = {
    LEADS: { accounts: 105 },
    PROSPECTS: { accounts: 43 },
    WON: { accounts: 102, color: "#3c7cc0" },
    OPPORTUNITIES: { accounts: 30 },
    funnelPercentages: { leads: 90, prospects: 50, won: 60, opportunities: 10 },
    pipelineData: {
      SALES_READY: { accounts: 105 },
      PREPARE_QUALIFY: { accounts: 43 },
      PRESENT: { accounts: 102 },
      FOLLOW_UP: { accounts: 102 },
      pipeline: 2.2,
      pipelinePercentages: {
        SALES_READY: 22,
        PREPARE_QUALIFY: 22,
        PRESENT: 22,
        FOLLOW_UP: 22,
      },
    },
  };

  const selectOptions = ['All Time']; //['All Time', 'YTD', 'MTD'];
  const selectOptions2 = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  React.useEffect(() => {
    if (tab === "lead") {
      setSelectedTab(Tab.LEAD);
    }
  }, [tab]);

  // Reset list when searchTerm changes
  React.useEffect(() => {
    // Only clear cache if search term actually changed (not on initial mount)
    if (prevSearchTermRef.current !== null && prevSearchTermRef.current !== debouncedSearchTerm) {
      console.log('Clearing cache due to search term change:', prevSearchTermRef.current, '->', debouncedSearchTerm);
      clearCache();
    }
    prevSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm, ]);


  React.useEffect(() => {
    if (prevSelectedFilterRef.current !== null && prevSelectedFilterRef.current !== selectedFilter) {
      clearCache();
    }
    prevSelectedFilterRef.current = selectedFilter;
    store.setPractitionersCarouselFilter(selectedFilter);
  }, [selectedFilter]);

  // Clear state when switching tabs to prevent data mixing
  React.useEffect(() => {
    if (selectedTab != Tab.ALL) {
      // Clear practitioners state when switching to LEAD tab
      setPractitioners([]);
      setPage(1);
      setHasMore(true);
    } if (selectedTab != Tab.LEAD) {
      // Clear leads state when switching to other tabs
      setLeads([]);
      setLeadPage(1);
      setHasMoreLeads(true);
    }
    
    // Restore total count when switching back to ALL tab
    if (selectedTab === Tab.ALL) {
      const cachedTotal = store.getCachedTotalCount();
      if (cachedTotal > 0) {
        setTotalCount(cachedTotal);
      }
    }
  }, [selectedTab]);

  // Fetch practitioners when page or searchTerm changes
  React.useEffect(() => {
    const controller = new AbortController();

    const fetchPractitioners = async () => {
      // Only block pagination requests during loading, not new searches
      if (selectedTab === Tab.LEAD) {
        if (leadPage > 1 && isLeadsLoading) return;
      } else {
        if (page > 1 && isLoading) return;
      }

      //ugly fix
      let toggle = true;

      try {
        if (selectedTab === Tab.LEAD) {
          // Check cache first for leads
          const cachedLeads = store.getLeadsCache(leadPage);
          if (cachedLeads) {
            if (leadPage === 1) {
              setLeads(cachedLeads);
            } else {
              setLeads((prev: any[]) => [...prev, ...cachedLeads]);
            }
            return;
          }

          setIsLeadsLoading(true);
          await postData(
            '/api/get_practitioners_for_chat_lead',
            {
              fmc_email: store.impersonatedUser,
              search: debouncedSearchTerm,
              page: leadPage,
              limit: LIMIT,
            },
            controller.signal
          ).then((data) => {
            setTotalLeads(data.total);
            
            // Cache the results
            store.setLeadsCache(leadPage, data.practitioners || []);
            console.log("leadPage", leadPage);
            if (leadPage === 1) {
              setLeads(data.practitioners || []);
            } else {
              setLeads((prev: any[]) => [...prev, ...(data.practitioners || [])]);
            }
            setIsLeadsLoading(false);

            if (!data.practitioners || data.practitioners.length < LIMIT) {
              setHasMoreLeads(false);
            }
          });
        }
        else {
          // Check cache first for practitioners
          const cachedPractitioners = store.getPractitionersCache(page);
          console.log('Checking cache for page:', page, 'cached:', !!cachedPractitioners, 'cache size:', Object.keys(store.practitionersCache).length);
          if (cachedPractitioners) {
            console.log('Using cached data for page:', page);
            if (page === 1) {
              setPractitioners(cachedPractitioners);
              // Restore total count from cache when using cached data
              const cachedTotal = store.getCachedTotalCount();
              if (cachedTotal > 0) {
                setTotalCount(cachedTotal);
              }
            } else {
              console.log(cachedPractitioners, "cachedPractitioners");
              setPractitioners((prev: any[]) => [...prev, ...cachedPractitioners]);
            }
            return;
          }

          console.log('Cache miss for page:', page, 'making API call');
          setIsLoading(true);
          const data = await postData(
            '/api/get_practitioners_for_chat',
            {
              fmc_email: store.impersonatedUser,
              search: debouncedSearchTerm,
              page: page,
              limit: LIMIT,
              carouselFilter: selectedFilter,
              dropdownFilter: multiSelectValue,
              alias: store.impersonatedUserAlias,
            },
            controller.signal
          ).then((data) => {
            setTotalCount(data.total);
            store.setCachedTotalCount(data.total);
            
            // Cache the results
            console.log('Caching data for page:', page, 'data length:', data.practitioners?.length);
            store.setPractitionersCache(page, data.practitioners || []);

            if (page === 1) {
              setPractitioners(data.practitioners || []);
            } else {
              setPractitioners((prev: any[]) => [...prev, ...(data.practitioners || [])]);
            }

            if (!data.practitioners || data.practitioners.length < LIMIT) {
              setHasMore(false);
            }
          });
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching practitioners:', error);
        } else {
          toggle = false;
        }
      } finally {
        if (selectedTab != Tab.LEAD && toggle) {
          setIsLoading(false);
        }
      }
      
    };

    fetchPractitioners();

    return () => controller.abort();
  }, [page, debouncedSearchTerm, selectedTab, selectedFilter, multiSelectOpen]);

  const clearCache = () => {
    setPractitioners([]);
    setPage(1);
    setLeadPage(1);
    setHasMoreLeads(true);
    setLeads([]);
    setHasMore(true);
    store.clearAllCache();
  }
  // Intersection observer to load next page when near bottom
  const observer = React.useRef<IntersectionObserver | null>(null);
  const lastPractitionerRef = React.useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prev) => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Fetch funnel data
  React.useEffect(() => {
    const fetchFunnelData = async () => {
      try {
        const data = await postData(
          '/api/e/winback_funnel_data',
          { fmc_email: store.impersonatedUser }
        );
        setFunnelData(data);
      } catch (_err) {
        // handle/log error if needed
      }
    };
    fetchFunnelData();
  //}, [postData]);
  }, []);

  // Build list of filtered practitioners
  const getFilteredPractitioners = () => {
    let filtered = practitioners.slice();

    // 1) Tab filtering
    if (selectedTab === Tab.RECENT) {
      filtered = filtered.filter(p => p.last_contact);
    }
    // if (selectedTab === Tab.LEAD) {
    //   filtered = filtered.filter(
    //     p =>
    //       p.status === "LEAD-INCOMPLETE" ||
    //       p.status === "LEAD-READY2SUBMIT"
    //   );
    // }


    return filtered;
  };

  // RENDERING LOGIC FOR TABS
  const renderTabContent = (): React.ReactNode => {
    // ------- 1) FUNNEL TAB -------
    if (selectedTab === Tab.FUNNEL) {
      // If pipeline not clicked yet, show funnel
      if (!pipelineIsClicked) {
        return (
          <>
           
            <div className={styles.opportunityText}>
              {/*
              Your total opportunity is $2.2M across{" "}
              <strong>{practitioners.length} active accounts</strong>
              */}
            </div>
            
            {/*
            <div className={styles.funnelHeader}>
              <div className={styles.funnelHeaderLeft}>Recent account as of</div>
              <select
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                className={styles.periodSelect}
              >
                {selectOptions.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
            </div>
            */}

            {/*
              WARNING: If funnelData is null or data.payload is undefined,
              you might crash here. Always check for funnelData before accessing .payload
            */}
            {funnelData?.payload && (
              <Funnel
                setPipelineIsClicked={setPipelineIsClicked}
                funnelData={funnelData.payload}
              />
            )}
          </>
        );
      }

      // If pipeline arrived at by clicking in Funnel chart
      return (
        <>
          <div className={styles.salesPipeline}>
            <div className={styles.expandIcon}>
              <ExpandIcon
                onClick={() => setPipelineIsClicked(false)}
                width={20}
                height={20}
                style={{ color: "#4F7D9C" }}
              />
            </div>
            <div style={{ paddingLeft: "15px", marginBottom: "15px" }}>
              <div className={styles.salesPipelineText}>Sales Pipeline</div>
              <div className={styles.salesPipelineSubText}>
                Your total opportunity is $2.2M across{" "}
                <strong>{practitioners.length} active accounts</strong>
              </div>
            </div>
          </div>

          <div className={styles.funnelHeader}>
            <div className={styles.funnelHeaderLeft}>By Expected Close</div>
            <select
              value={selectedOption2}
              onChange={(e) => setSelectedOption2(e.target.value)}
              className={styles.periodSelect}
            >
              {selectOptions2.map((period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </select>
          </div>

          <Pipeline pipelineData={dadosFunel.pipelineData} />
        </>
      );
    }

    // ------- 2) ALL / RECENT / LEAD TABS -------
    // Build the filtered list (will be the same for ALL, RECENT, LEAD)
    const filteredPractitioners = getFilteredPractitioners();

    return (
      <>
        {/* === SEARCH BAR (only show for ALL, RECENT, LEAD) === */}
        <div className={styles.searchWrapper}>
          <MagnifyingGlassIcon width={20} height={20} />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <div style={{zIndex: 1000, cursor: 'pointer', position: 'relative',marginRight: "10px"}} 
               data-filter-container
               onClick={() => {
                if (multiSelectOpen) {
                  // If dropdown is open, close it and trigger onClose logic
                  setMultiSelectOpen(false);
                  clearCache();
                } else {
                  // If dropdown is closed, open it
                  setMultiSelectOpen(true);
                }
               }}
          >
            <FadersIcon 
              width={25} 
              height={25} 
              style={{color: multiSelectValue.length < 6 ? "#4F7D9C" : "#8B8B8B"}}
            />
            

          </div>

          {(searchTerm.trim() !== "") && (selectedTab !== Tab.LEAD) && (
            <button
              className={styles.clearButton}
              onClick={() => setSearchTerm("")}
            >
              X
            </button>
          )}
          {selectedTab === Tab.LEAD && (
            <button
              className={styles.addButton}
              onClick={() => {
                navigate(
                  `/?${new URLSearchParams({
                    prompt: "help me create a new lead",
                    toggle: "false",
                  }).toString()}`
                );
              }}
            >
              Add
            </button>
          )}
          <FilterDropdown
              isOpen={multiSelectOpen}
              selectedValues={multiSelectValue}
              onValueChange={setMultiSelectValue}
              options={["Name", "Address", "City", "State", "Specialty", "Email", "Zipcode"]}
              onClose={() =>{
                setMultiSelectOpen(false);
                clearCache();
              }}
            />
        </div>
        <p className={styles.infoText}>
          {selectedTab == Tab.ALL ? (
            <FilterCarousel
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
            />
            ) : (
              <></>
            )
          }
          <div className={styles.filterText}>
            <CheckIcon className={styles.checkIconSmall} />
            Has Lat & Long: Verified Check-In Possible       
          </div>
        </p>          

        {/* === CUSTOMER LIST === */}
        {(selectedTab === Tab.LEAD ? leads : practitioners)?.length === 0 
        && (selectedTab === Tab.LEAD ? !isLeadsLoading : !isLoading)
        && (selectedTab === Tab.LEAD && store.getLeadsCache(1)) && 
        (
          <div>No results</div>
        )}
        {(selectedTab === Tab.LEAD ? leads : practitioners)?.length > 0 &&  (
          /*filteredPractitioners.map((customer: any, index: number) => (
            <div
              className={styles.customer}
              key={customer.id}
              onClick={() => onCustomerClick(customer)}
            >
              <p className={styles.practitionerName}>
                {`${index + 1}. ${customer.primary_name}`}
              </p>
              {customer.status === "LEAD-INCOMPLETE" && (
                <div className={styles.leadFlag}>
                  <span>LEAD</span>
                </div>
              )}
              {customer.status === "LEAD-READY2SUBMIT" && (
                <div className={styles.leadFlagComplete}>
                  <span>LEAD</span>
                </div>
              )}
              <p className={styles.category}>{customer.specialty}</p>
            </div>
          )*/
         (selectedTab === Tab.LEAD ? leads : practitioners)?.map((customer: any, index: number) => {
          // 1) Determine the data‐source pill text:
          let dataSourcePillText: string | null = null;
          switch (customer.data_source) {
            case "PRACTITIONER_ACCOUNT_VIEW":
              dataSourcePillText = null; // no pill for this case
              break;
            case "PRACTITIONER_ACCOUNT_VIEW_NM":
              dataSourcePillText = "Badger";
              break;
            case "PRACTITIONER_DISTRIBUTOR_ACCOUNT_VIEW":
              switch(customer.name_dfh_channel){
                case "DFH":
                  dataSourcePillText = `Dist: ${customer.name_dfh_channel}`;
                  break;
                default:
                  dataSourcePillText = `${customer.name_dfh_channel}`;
                  break;
              }                
              break;
            default:
              dataSourcePillText = null;
          }

          const isNearBottom = index > (selectedTab === Tab.LEAD ? leads.length : practitioners.length) - 50;
          return (
            <div
              ref={isNearBottom ? lastPractitionerRef : undefined}
              className={styles.customer}
              key={customer.id}
              onClick={() => onCustomerClick(customer)}
            >
              <div className={styles.leftWrapper}>
                  {/*<div className={styles.nameRow}>*/}
                <div className={styles.counterColumn}>
                  <span className={styles.practitionerCounter}>
                  {`${index + 1}.`}
                  </span>
                </div>
                <div className={styles.leftColumn}>
                  <span className={styles.practitionerName}>
                    {`${customer.primary_name}`}
                  </span>
                  <span className={styles.category}>{customer.specialty}</span>                
                </div>
              </div>              
              <div className={styles.rightColumn}>
                {customer.latitude && customer.longitude && (
                  <CheckIcon className={styles.checkIcon} />                
                )}
                {/*
                  2) If this practitioner has a "data source pill," render it here.
                     It will inherit CSS from .dataSourcePill (red background, white text, rounded).
                */}
                {dataSourcePillText && (
                  <div className={styles.dataSourcePill}>
                    {dataSourcePillText}
                  </div>
                )}
                
              </div>

              {customer.status === "LEAD-INCOMPLETE" && (
                <div className={styles.leadFlag}>
                  <span>LEAD</span>
                </div>
              )}
              {customer.status === "LEAD-READY2SUBMIT" && (
                <div className={styles.leadFlagComplete}>
                  <span>LEAD</span>
                </div>
              )}

              {/*<p className={styles.category}>{customer.specialty}</p>*/}
            </div>
          );
         })  
        )}
        {(selectedTab === Tab.LEAD ? isLeadsLoading : isLoading) && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
            <Loader size="lg" />
          </div>
        )}
      </>
    );
  };

  // =============== FINAL RENDER ===============
  return (
    <div className={styles.wrapper}>
      <h2 className={styles.header}>Your Accounts</h2>
      <p className={styles.customerCounter}>
        {totalCount} Accounts        
      </p>
      
      <div className={styles.horizontalScrollWrapper}>
        {/* ALL TAB */}
        <div
          className={`
            ${styles.horizontalScrollItemWrapper}
            ${styles.tab}
            ${selectedTab === Tab.ALL ? styles.selectedTab : ""}
          `}
          onClick={() => {
            setSelectedTab(Tab.ALL);
            setPipelineIsClicked(false);
          }}
        >
          <UserIcon width={24} height={24} />
          <span>All</span>
        </div>

        {/*
        //TODO: uncomment this when I want to show recent tab of updated practitioners
        <div 
          className={`${styles.horizontalScrollItemWrapper} ${styles.tab} ${selectedTab === Tab.RECENT ? styles.selectedTab : ''}`}
          onClick={() => setSelectedTab(Tab.RECENT)}
        >
          <ClockCounterClockwiseIcon width={24} height={24} />
          <span>Recent</span>
        </div>
        */}
        
        {/* LEAD TAB */}
        <div
          className={`
            ${styles.horizontalScrollItemWrapper}
            ${styles.tab}
            ${selectedTab === Tab.LEAD ? styles.selectedTab : ""}
          `}
          onClick={() => {
            setSelectedTab(Tab.LEAD);
            setPipelineIsClicked(false);
          }}
        >
          <LeadIcon width={24} height={24} />
          <span>Leads</span>
        </div>

        {/* FUNNEL TAB */}
        <div
          className={`
            ${styles.horizontalScrollItemWrapper}
            ${styles.tab}
            ${selectedTab === Tab.FUNNEL ? styles.selectedTab : ""}
          `}
          onClick={() => {
            setSelectedTab(Tab.FUNNEL);
            setPipelineIsClicked(false);
          }}
        >
          {selectedTab === Tab.FUNNEL ? (
            <BlueFunnelIcon width={24} height={24} />
          ) : (
            <FunnelIcon width={24} height={24} />
          )}
          <span>Funnel</span>
        </div>

      </div>
      
      {renderTabContent()}
    </div>
  );
};

export default ListCustomers;
